/**
 * ExamBooking — public exam discount eligibility page (/exam-booking)
 *
 * Stage 1: form (name, email, phone, nationality, exam, preferred month)
 * Stage 2: result screen (no reload) —
 *   India:         eligible + original price strikethrough + special price + contact CTAs
 *   International: "details received, team will contact in 24-48 hours"
 *
 * Email links land here with ?source=email_trigger; organic visits submit "organic".
 * Email field pre-fills from authUser; Authorization header sent when authToken exists
 * so the backend can link the request to the logged-in user.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GraduationCap, CheckCircle2, ClipboardCheck, Loader2,
  Phone, Mail, MessageCircle,
} from 'lucide-react';
import { API_BASE } from '../services/api';

const WHATSAPP_URL =
  'https://api.whatsapp.com/send/?phone=919001991227&text&type=phone_number&app_absent=0';
const CONTACT_PHONE = '+91-9001991227';
const CONTACT_EMAIL = 'info@grras.tech';

const inputCls =
  'w-full bg-slate-800/80 border border-white/10 rounded-lg px-4 py-3 text-white ' +
  'placeholder-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500';

// <input type="month"> degrades to plain text in desktop Firefox/Safari, letting
// users submit values that fail the backend's YYYY-MM check — use selects instead.
const MONTH_OPTIONS = [
  ['01', 'January'], ['02', 'February'], ['03', 'March'], ['04', 'April'],
  ['05', 'May'], ['06', 'June'], ['07', 'July'], ['08', 'August'],
  ['09', 'September'], ['10', 'October'], ['11', 'November'], ['12', 'December'],
];

const ExamBooking = () => {
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') === 'email_trigger' ? 'email_trigger' : 'organic';

  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // saved booking record → stage 2

  const storedUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('authUser') || 'null'); }
    catch { return null; }
  }, []);

  const { defaultMonth, defaultYear, yearOptions } = useMemo(() => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1); // next month from today
    return {
      defaultMonth: String(next.getMonth() + 1).padStart(2, '0'),
      defaultYear: String(next.getFullYear()),
      yearOptions: [0, 1, 2].map((i) => String(now.getFullYear() + i)),
    };
  }, []);

  const [prefMonth, setPrefMonth] = useState(defaultMonth);
  const [prefYear, setPrefYear] = useState(defaultYear);
  const [monthError, setMonthError] = useState(false);

  const [form, setForm] = useState({
    full_name: storedUser ? `${storedUser.first_name || ''} ${storedUser.last_name || ''}`.trim() : '',
    email: storedUser?.email || '',
    phone: '',
    nationality: 'india',
    exam_code: '',
    preferred_month: `${defaultYear}-${defaultMonth}`,
  });

  const setPreferred = (month, year) => {
    setPrefMonth(month);
    setPrefYear(year);
    setMonthError(false);
    setForm((f) => ({ ...f, preferred_month: month && year ? `${year}-${month}` : '' }));
  };

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/exam-booking/pricing`);
        if (!res.ok) throw new Error('Failed to load exams');
        const data = await res.json();
        setExams(data);
        if (data.length > 0) {
          setForm((f) => (f.exam_code ? f : { ...f, exam_code: data[0].exam_code }));
        }
      } catch (e) {
        console.error(e);
        toast.error('Failed to load the exam list — please refresh the page');
      } finally {
        setLoadingExams(false);
      }
    };
    fetchPricing();
  }, []);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prefMonth || !prefYear) {
      setMonthError(true);
      return;
    }
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim()
        || !form.exam_code || !form.preferred_month) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const authToken = localStorage.getItem('authToken');
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      const res = await fetch(`${API_BASE}/api/exam-booking/submit`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...form, source }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        let msg = 'Submission failed — please try again.';
        if (typeof data.detail === 'string') {
          msg = data.detail;
        } else if (Array.isArray(data.detail) && data.detail.length > 0) {
          msg = data.detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
        throw new Error(msg);
      }
      setResult(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Stage 2: result screens ────────────────────────────────────────────────
  if (result) {
    const exam = exams.find((e) => e.exam_code === result.exam_code);
    const isIndia = result.nationality === 'india';
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full bg-slate-800/70 border border-white/10 rounded-2xl p-8 text-center backdrop-blur">
          {isIndia ? (
            <>
              <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-6">✅ You're Eligible!</h1>

              <div className="bg-slate-900/60 border border-white/10 rounded-xl p-6 mb-6">
                <p className="text-slate-400 text-sm mb-2">
                  {exam?.exam_name || result.exam_code} Original Fee:{' '}
                  <span className="line-through text-slate-500">
                    ₹{Number(exam?.original_price_inr || 0).toLocaleString('en-IN')}
                  </span>
                </p>
                <p className="text-orange-400 text-3xl font-bold">
                  Your Special Price: ₹{Number(result.discounted_price_shown || 0).toLocaleString('en-IN')}
                </p>
              </div>

              <p className="text-slate-300 mb-4">To book your exam, contact our team:</p>
              <div className="text-slate-300 space-y-2 mb-6 text-sm">
                <p className="flex items-center justify-center gap-2"><Phone className="w-4 h-4 text-orange-400" /> {CONTACT_PHONE}</p>
                <p className="flex items-center justify-center gap-2"><Mail className="w-4 h-4 text-orange-400" /> {CONTACT_EMAIL}</p>
                <p className="flex items-center justify-center gap-2"><MessageCircle className="w-4 h-4 text-orange-400" /> WhatsApp</p>
              </div>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
              >
                <MessageCircle className="w-5 h-5" /> Book via WhatsApp
              </a>
            </>
          ) : (
            <>
              <ClipboardCheck className="w-16 h-16 text-orange-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-4">📋 Details Received!</h1>
              <p className="text-slate-300 leading-relaxed mb-6">
                Our customer care team will contact you within 24-48 hours
                regarding your discount eligibility.
              </p>
              <p className="flex items-center justify-center gap-2 text-slate-300">
                <Mail className="w-4 h-4 text-orange-400" /> {CONTACT_EMAIL}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Stage 1: form ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-12">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-orange-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-7 h-7 text-orange-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Exam Discount Eligibility</h1>
          <p className="text-slate-400">
            Check your eligibility for a Red Hat exam discount — fill in the form
            and get an instant result.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-slate-800/70 border border-white/10 rounded-2xl p-6 sm:p-8 space-y-5 backdrop-blur"
        >
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Full Name *</label>
            <input
              type="text" required maxLength={100} value={form.full_name}
              onChange={setField('full_name')} placeholder="Your full name" className={inputCls}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Email *</label>
            <input
              type="email" required value={form.email}
              onChange={setField('email')} placeholder="you@example.com" className={inputCls}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Phone / WhatsApp *</label>
            <input
              type="tel" required minLength={7} maxLength={20} value={form.phone}
              onChange={setField('phone')} placeholder="+91 9XXXXXXXXX" className={inputCls}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Nationality *</label>
            <select value={form.nationality} onChange={setField('nationality')} className={inputCls}>
              <option value="india">India</option>
              <option value="international">International</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Exam Type *</label>
            {loadingExams ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-3">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading exams…
              </div>
            ) : (
              <select required value={form.exam_code} onChange={setField('exam_code')} className={inputCls}>
                {exams.map((e) => (
                  <option key={e.exam_code} value={e.exam_code}>{e.exam_name}</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Preferred Exam Month *</label>
            <div className="grid grid-cols-2 gap-3">
              <select
                value={prefMonth} onChange={(e) => setPreferred(e.target.value, prefYear)}
                className={inputCls} aria-label="Preferred month"
              >
                <option value="">Month</option>
                {MONTH_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <select
                value={prefYear} onChange={(e) => setPreferred(prefMonth, e.target.value)}
                className={inputCls} aria-label="Preferred year"
              >
                <option value="">Year</option>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            {monthError && (
              <p className="text-red-400 text-xs mt-2">Please select both month and year</p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || loadingExams}
            className="w-full bg-orange-600 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {submitting
              ? (<><Loader2 className="w-5 h-5 animate-spin" /> Checking…</>)
              : 'Check My Discount Eligibility'}
          </button>

          <p className="text-center text-xs text-slate-500">
            Have any questions? Raise a ticket in the Help section — {CONTACT_EMAIL}
          </p>
        </form>
      </div>
    </div>
  );
};

export default ExamBooking;
